import { useState } from 'react';
import Seo from '@/components/Seo';
import { Breadcrumb } from '@/components/layout/Breadcrumb';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { Slider } from '@/components/ui/slider';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { UploadDropzone } from '@/components/tools/UploadDropzone';
import { ToolLongForm } from '@/components/tools/ToolLongForm';
import { ToolFAQ } from '@/components/tools/ToolFAQ';
import { AdSlot } from '@/components/ads/AdSlot';
import { compressImageApi, absoluteApiUrl } from '@/lib/api';
import { downloadFromUrl, humanBytes } from '@/lib/download';
import { AlertCircle, CheckCircle2, Download, Layers, Loader2, RotateCcw, ShieldCheck } from 'lucide-react';
import { TOOL_PAGE } from '@/constants/testIds';
import { toast } from 'sonner';

const FAQS = [
    { q: 'Which formats are supported?', a: 'JPG, PNG, and WebP. If your PNG has no transparency, we’ll output a JPG for a much smaller file. PNGs with transparency stay as PNG.' },
    { q: 'What does the quality slider actually do?', a: 'It controls how aggressively the JPG/WebP encoder discards subtle detail. 70 is a good balance for the web; 85–90 is near-lossless; anything under 50 becomes visibly softer on close inspection.' },
    { q: 'How much smaller will my file be?', a: 'For typical photographs, expect 60–90% reduction at quality 70. Screenshots and flat graphics see less benefit because PNG already compresses them well.' },
    { q: 'Do you resize the image?', a: 'No. The pixel dimensions stay the same. Only the encoding changes. Use the Resize Image tool if you also need smaller dimensions.' },
    { q: 'How are my files handled?', a: 'They’re processed in an isolated worker and deleted from disk immediately after compression.' },
];

const HOW = [
    'Upload a JPG, PNG, or WebP image and choose a quality level. The lower the number, the more aggressive the compression — and the smaller the resulting file. For most photos on the web, quality 70 hits the sweet spot: dramatically smaller, near-indistinguishable from the original at typical screen sizes.',
    'Our pipeline reads your image with Pillow, respects the EXIF orientation, decides on the best output format for the content, and re-encodes it with well-tuned parameters. For PNGs without transparency, we’ll suggest JPG output to unlock much larger savings.',
    'When compression finishes, we tell you the original size, the new size, and the compression ratio so you can decide whether to save the result or dial the quality up a notch. Download streams straight to your browser, and our copy is removed as soon as you’ve got yours.',
    'The tool works on phones just as well as on laptops. You can compress hundreds of images without ever creating an account or leaving a trail on our servers.',
];
const BENEFITS = [
    'Images are usually the heaviest thing a webpage ships. Reducing image weight is the single most effective performance improvement you can make. Google’s Core Web Vitals — the ranking factors around perceived load speed — are dominated by how quickly your largest image renders. Every 100 KB you shave off can move your LCP score noticeably.',
    'There’s also a mobile data angle. Many of your visitors are on metered plans or slow connections. Serving them a 350 KB hero instead of a 3 MB one is a small kindness that adds up across a busy site — and it makes your product feel faster on the devices that matter most.',
    'Finally, smaller images cost less to store, serve, and back up. Whether you’re running a personal site or a busy commercial platform, image compression pays for itself the day you turn it on.',
];
const TIPS = [
    'Try quality 70 first — it’s the web default that works for almost every photo.',
    'For hero images, aim for under 250 KB. For thumbnails, under 40 KB.',
    'If you plan to further edit the image, keep a full-quality master somewhere else and compress a copy.',
];

export default function ImageCompressorPage() {
    const [file, setFile] = useState(null);
    const [quality, setQuality] = useState([70]);
    const [progress, setProgress] = useState(0);
    const [status, setStatus] = useState('idle');
    const [error, setError] = useState('');
    const [result, setResult] = useState(null);

    const reset = () => {
        setFile(null);
        setProgress(0);
        setStatus('idle');
        setError('');
        setResult(null);
    };

    const onFile = (f) => {
        setFile(f);
        setStatus('idle');
        setError('');
        setResult(null);
    };

    const run = async () => {
        if (!file) return;
        setStatus('uploading');
        setError('');
        setProgress(0);
        try {
            const q = quality[0];
            const envelope = await compressImageApi(file, q, setProgress);
            setResult(envelope);
            setStatus('done');
            toast.success('Compression complete.');
        } catch (err) {
            const msg = err?.response?.data?.detail || err?.message || 'Something went wrong.';
            setError(msg);
            setStatus('error');
            toast.error(msg);
        }
    };

    const structured = {
        '@context': 'https://schema.org',
        '@type': 'SoftwareApplication',
        name: 'UtilityTool — Image Compressor',
        applicationCategory: 'Utility',
        operatingSystem: 'Any',
        offers: { '@type': 'Offer', price: '0', priceCurrency: 'USD' },
    };

    return (
        <>
            <Seo
                title="Image Compressor"
                description="Compress JPG, PNG, and WebP images with a quality slider. Reduce file size by up to 90% for faster websites, without losing visible quality."
                canonical="/tools/image-compressor"
                structuredData={structured}
            />
            <section className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 pt-10 pb-14">
                <Breadcrumb items={[{ label: 'Tools', to: '/tools' }, { label: 'Image Compressor' }]} />
                <div className="mt-4 flex items-start gap-4">
                    <span className="inline-flex items-center justify-center w-12 h-12 rounded-xl bg-[hsl(var(--accent))] text-[hsl(var(--primary))]">
                        <Layers className="w-6 h-6" />
                    </span>
                    <div>
                        <h1 className="text-3xl sm:text-4xl font-semibold tracking-tight text-slate-900">
                            Image Compressor
                        </h1>
                        <p className="mt-2 text-slate-600 max-w-2xl">
                            Shrink JPG, PNG, and WebP images with a quality slider. See
                            exactly how much smaller your file gets.
                        </p>
                        <div className="mt-3 flex flex-wrap gap-2">
                            <Badge variant="secondary" className="bg-slate-100 text-slate-700">Up to 90% smaller</Badge>
                            <Badge variant="secondary" className="bg-slate-100 text-slate-700">Quality control</Badge>
                            <Badge variant="secondary" className="bg-slate-100 text-slate-700">Up to 25 MB</Badge>
                        </div>
                    </div>
                </div>

                <div className="mt-8 grid grid-cols-1 lg:grid-cols-[1.2fr_0.8fr] gap-6 lg:gap-8">
                    <Card className="p-5 sm:p-6 border-border/80">
                        <UploadDropzone
                            accept=".jpg,.jpeg,.png,.webp,image/*"
                            file={file}
                            onFile={onFile}
                            onClear={reset}
                            disabled={status === 'uploading'}
                            hint="Accepted formats: .jpg, .jpeg, .png, .webp · Max 25 MB"
                        />

                        <div className="mt-6">
                            <div className="flex items-center justify-between">
                                <label htmlFor="quality" className="text-sm font-medium text-slate-800">
                                    Quality
                                </label>
                                <span className="text-sm text-slate-600 tabular-nums">
                                    {quality[0]}
                                </span>
                            </div>
                            <Slider
                                id="quality"
                                data-testid={TOOL_PAGE.qualitySlider}
                                min={20}
                                max={95}
                                step={1}
                                value={quality}
                                onValueChange={setQuality}
                                className="mt-3"
                            />
                            <div className="mt-2 flex items-center justify-between text-xs text-slate-500">
                                <span>Smaller file</span>
                                <span>Better quality</span>
                            </div>
                        </div>

                        {status === 'uploading' ? (
                            <div className="mt-6">
                                <div className="flex items-center justify-between text-sm mb-2">
                                    <span className="font-medium text-slate-800">Compressing…</span>
                                    <span className="text-slate-500">{progress}%</span>
                                </div>
                                <Progress value={progress} className="h-2" />
                            </div>
                        ) : null}
                        {status === 'error' && error ? (
                            <Alert variant="destructive" className="mt-6" data-testid={TOOL_PAGE.errorAlert}>
                                <AlertCircle className="w-4 h-4" />
                                <AlertTitle>We couldn’t compress this image</AlertTitle>
                                <AlertDescription>{error}</AlertDescription>
                            </Alert>
                        ) : null}
                        {status === 'done' && result ? (
                            <div className="mt-6 rounded-xl border border-emerald-200 bg-emerald-50 p-4" data-testid={TOOL_PAGE.resultCard}>
                                <div className="flex items-center gap-2 text-emerald-700">
                                    <CheckCircle2 className="w-4 h-4" />
                                    <span className="text-sm font-medium">Compression complete</span>
                                </div>
                                <div className="mt-3 grid grid-cols-2 sm:grid-cols-3 gap-3 text-sm">
                                    <MetaCell label="Original" value={humanBytes(result.original_size)} />
                                    <MetaCell label="Compressed" value={humanBytes(result.compressed_size)} />
                                    <MetaCell label="Ratio" value={result.ratio || '–'} />
                                </div>
                                <div className="mt-4 flex items-center justify-between gap-3">
                                    <p className="text-xs text-slate-500 truncate">{result.filename}</p>
                                    <Button
                                        data-testid={TOOL_PAGE.downloadButton}
                                        onClick={() => downloadFromUrl(absoluteApiUrl(result.download_url), result.filename)}
                                        className="bg-[hsl(var(--primary))] hover:bg-[hsl(var(--link-hover))] text-white"
                                    >
                                        <Download className="w-4 h-4 mr-2" /> Download
                                    </Button>
                                </div>
                            </div>
                        ) : null}

                        <div className="mt-6 flex flex-col sm:flex-row gap-3">
                            <Button
                                onClick={run}
                                disabled={!file || status === 'uploading'}
                                data-testid={TOOL_PAGE.runButton}
                                className="h-11 bg-slate-900 hover:bg-slate-800 text-white"
                            >
                                {status === 'uploading' ? (
                                    <>
                                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                                        Compressing…
                                    </>
                                ) : (
                                    'Compress image'
                                )}
                            </Button>
                            <Button variant="secondary" onClick={reset} className="h-11">
                                <RotateCcw className="w-4 h-4 mr-2" /> Reset
                            </Button>
                        </div>
                    </Card>

                    <aside className="space-y-4">
                        <Card className="p-5 border-border/80 bg-[hsl(var(--surface-2))]">
                            <div className="flex items-center gap-2 text-slate-800">
                                <ShieldCheck className="w-4 h-4 text-[hsl(var(--primary))]" />
                                <span className="text-sm font-semibold">Your image is private</span>
                            </div>
                            <p className="mt-2 text-sm text-slate-600 leading-relaxed">
                                Files are deleted from our servers immediately after
                                compression — we never keep or log the images you upload.
                            </p>
                        </Card>
                        <Card className="p-5 border-border/80">
                            <p className="text-sm font-semibold text-slate-900">Also useful</p>
                            <ul className="mt-2 space-y-1.5 text-sm">
                                <li><a href="/tools/jpg-to-png" className="text-[hsl(var(--primary))] hover:underline">JPG to PNG</a></li>
                                <li><a href="/tools/word-to-pdf" className="text-[hsl(var(--primary))] hover:underline">Word to PDF</a></li>
                                <li><a href="/blog/how-web-performance-affects-seo" className="text-[hsl(var(--primary))] hover:underline">Guide: Web performance & SEO</a></li>
                            </ul>
                        </Card>
                    </aside>
                </div>

                <ToolLongForm howItWorks={HOW} benefits={BENEFITS} tips={TIPS} />
                <AdSlot placement="tool" />
                <ToolFAQ items={FAQS} />
            </section>
        </>
    );
}

function MetaCell({ label, value }) {
    return (
        <div className="rounded-lg border border-emerald-200/70 bg-white/70 p-3">
            <p className="text-xs text-slate-500">{label}</p>
            <p className="mt-0.5 text-sm font-semibold text-slate-900">{value}</p>
        </div>
    );
}
